import { Box, Button, Checkbox, HStack, Text, VStack } from '@chakra-ui/react';
import { ProductSelector } from '@/components/flows/FlowEditor/properties/ProductSelector';
import type { ShopifyProduct } from '@/hooks/queries/use-shopify-products-query';
import { GiftProductThumb } from '@/pages/gifting/GiftProductThumb';
import { GiftReofferCatalogStatus, type GiftReofferReviewProduct } from '@fluenceur/types';

export type GiftReofferSelections = Record<string, string[]>;

interface GiftReofferProductEditorProps {
  products: GiftReofferReviewProduct[];
  selections: GiftReofferSelections;
  onChange: (selections: GiftReofferSelections) => void;
  onRemove: (productId: string) => void;
  removeLabel: string;
  statusLabel?: (product: GiftReofferReviewProduct) => string;
  onAddProduct?: (product: ShopifyProduct) => void;
  addLabel?: string;
}

/** Shared identifier-only editor; snapshots remain entirely server-owned. */
export const GiftReofferProductEditor = ({
  products,
  selections,
  onChange,
  onRemove,
  removeLabel,
  statusLabel,
  onAddProduct,
  addLabel,
}: GiftReofferProductEditorProps) => (
  <VStack align="stretch" gap="2">
    {products.map((product) => {
      const selected = new Set(selections[product.shopifyProductId] ?? []);
      return (
        <Box key={product.shopifyProductId} borderWidth="1px" borderRadius="md" p="3">
          <HStack justify="space-between" align="start">
            <HStack gap="3" minW="0">
              <GiftProductThumb imageUrl={product.imageUrl ?? null} size={36} />
              <Box minW="0">
                <Text fontWeight="600" overflowWrap="anywhere">
                  {product.title}
                </Text>
                {statusLabel ? (
                  <Text fontSize="xs" color="fg.muted">
                    {statusLabel(product)}
                  </Text>
                ) : null}
              </Box>
            </HStack>
            <Button
              size="xs"
              variant="plain"
              aria-label={`${removeLabel}: ${product.title}`}
              onClick={() => onRemove(product.shopifyProductId)}
            >
              {removeLabel}
            </Button>
          </HStack>
          {product.variants.length > 1 ? (
            <VStack align="stretch" mt="2" gap="1">
              {product.variants.map((variant) => {
                const invalid =
                  variant.status === GiftReofferCatalogStatus.MISSING ||
                  variant.status === GiftReofferCatalogStatus.NOT_ORDERABLE;
                return (
                  <Checkbox.Root
                    key={variant.shopifyVariantId}
                    checked={selected.has(variant.shopifyVariantId)}
                    disabled={invalid && !selected.has(variant.shopifyVariantId)}
                    onCheckedChange={(details) => {
                      const next = new Set(selected);
                      if (details.checked) next.add(variant.shopifyVariantId);
                      else next.delete(variant.shopifyVariantId);
                      onChange({ ...selections, [product.shopifyProductId]: [...next] });
                    }}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                    <Checkbox.Label>{variant.title}</Checkbox.Label>
                  </Checkbox.Root>
                );
              })}
            </VStack>
          ) : null}
        </Box>
      );
    })}
    {onAddProduct ? (
      <Box>
        <Text fontSize="sm" fontWeight="600" mb="2">
          {addLabel}
        </Text>
        <ProductSelector
          portalled={false}
          positioning={{ strategy: 'fixed', hideWhenDetached: true }}
          onToggle={onAddProduct}
          selectedProductIds={products.map((product) => product.shopifyProductId)}
        />
      </Box>
    ) : null}
  </VStack>
);
